import React, { lazy } from 'react';
import { Navigate, useParams } from 'react-router';
import PlatformModeRoute from './Components/PlatformModeRoute';

// Unwraps a named export for React.lazy, which only accepts a default.
const named = <T,>(p: Promise<T>, key: keyof T) =>
  p.then((m) => ({ default: m[key] as any }));

const Account = lazy(() => import(/* webpackChunkName: "account" */ './UI/Pages/AccountPage/account.page'));
const PacsSync = lazy(() => import(/* webpackChunkName: "analytics" */ './UI/Pages/AnalyticsPage1/analytics.page'));
const PatientManagement = lazy(() => import(/* webpackChunkName: "patients" */ './UI/Pages/PatientManagement/patient_management.page'));
const ExamManagement = lazy(() => import(/* webpackChunkName: "exams" */ './UI/Pages/ExamManagement/exam_management.page'));
const StudyInbox = lazy(() => import(/* webpackChunkName: "pacs-inbox" */ './UI/Pages/StudyInbox/study_inbox.page'));
const RealTimeInferencingV3 = lazy(() => import(/* webpackChunkName: "realtime" */ './UI/Pages/RealtimeInferencingPageV3/realtime-inferencing_v3.page'));
const RealtimeExamPage = lazy(() => import(/* webpackChunkName: "realtime-exam" */ './UI/Pages/RealtimeExamPage/realtime-exam.page'));
const BlindSweep = lazy(() => import(/* webpackChunkName: "sweep" */ './UI/Pages/BlindSweep/blind_sweep.page'));
const BlindLoops = lazy(() => import(/* webpackChunkName: "sweep-viewer" */ './UI/Pages/BlindLoops/BlindLoops'));
const ApiAsService = lazy(() => import(/* webpackChunkName: "api-as-service" */ './UI/Pages/ApiAsService/apiasservice.page'));
const ArchitectureDiagram = lazy(() => import(/* webpackChunkName: "architecture" */ './UI/Pages/ArchitecturePage/architecture'));
const AiAccelerate = lazy(() => import(/* webpackChunkName: "exam-viewer" */ './UI/Pages/AiAccelerate/AiAccelerate'));
const PatientTimeline = lazy(() => import(/* webpackChunkName: "patient-timeline" */ './UI/Pages/PatientManagement/PatientTimeline'));
const TestPage = lazy(() => import(/* webpackChunkName: "test" */ './UI/Pages/TestPage/test'));
const OrganizationPage = lazy(() => import(/* webpackChunkName: "organization" */ './UI/Pages/OrganizationPage/organization.page'));
const OrgStructurePage = lazy(() => import(/* webpackChunkName: "org-structure" */ './UI/Pages/OrgStructure/org_structure.page'));
const RolesPermissionsPage = lazy(() => import(/* webpackChunkName: "roles" */ './UI/Pages/RolesPermissions/roles_permissions.page'));
const TwoFactorSetupPage = lazy(() => import(/* webpackChunkName: "2fa-setup" */ './UI/Pages/TwoFactorSetup/TwoFactorSetup.page'));
const FeatureFlagsDebug = lazy(() => import(/* webpackChunkName: "feature-flags" */ './UI/Pages/FeatureFlagsDebug/feature_flags_debug.page'));
const BsExamAnalyticsPage = lazy(() => import(/* webpackChunkName: "exam-analytics" */ './UI/Pages/BsExamAnalyticsPage/bs-exam-analytics.page'));
const QuickInferencePage = lazy(() => import(/* webpackChunkName: "playground" */ './UI/Pages/QuickInferencePage/quick-inference.page'));
const QuickInferenceViewerPage = lazy(() => import(/* webpackChunkName: "playground-viewer" */ './UI/Pages/QuickInferencePage/quick-inference-viewer.page'));
const HomePage = lazy(() => import(/* webpackChunkName: "home" */ './UI/Pages/HomePage/home.page'));
const PredictionPerformancePage = lazy(() => import(/* webpackChunkName: "prediction-performance" */ './UI/Pages/PredictionPerformancePage/prediction-performance.page'));
const SurveysPage = lazy(() => import(/* webpackChunkName: "surveys" */ './UI/Pages/Surveys/surveys.page'));
const SurveyDetailPage = lazy(() => import(/* webpackChunkName: "survey-detail" */ './UI/Pages/Surveys/survey-detail.page'));
const SchedulingPage = lazy(() => import(/* webpackChunkName: "scheduling" */ './UI/Pages/SchedulingPage/PatientScheduling'));
const SubscriptionPage = lazy(() => import(/* webpackChunkName: "subscription" */ './UI/Pages/Subscription/subscription.page'));
const AuditTrailPage = lazy(() => import(/* webpackChunkName: "audit-trail" */ './UI/Pages/AuditTrail/audit-trail.page'));
const HelpCenterPage = lazy(() => import(/* webpackChunkName: "help" */ './help/HelpCenterPage'));

const AddPatientPage = lazy(() =>
  named(import(/* webpackChunkName: "patient-add" */ './UI/Pages/PatientManagement/AddPatientPage'), 'AddPatientPage')
);
const EditPatientPage = lazy(() =>
  named(import(/* webpackChunkName: "patient-edit" */ './UI/Pages/PatientManagement/EditPatientPage'), 'EditPatientPage')
);
const NewCaseWizard = lazy(() =>
  named(import(/* webpackChunkName: "new-case" */ './UI/ComponnentsUI/NewCaseReverseUpload/NewCaseOneStep'), 'NewCaseWizard')
);

function QuickInferenceLegacyRedirect() {
  const { sessionId } = useParams<{ sessionId: string }>();
  return <Navigate to={sessionId ? `/playground/${sessionId}` : '/playground'} replace />;
}

// Layer-1 page gates: the effective "entity.action"(s) that reveal each page.
// OR semantics (any one is enough). Omit `requiredPermissions` for pages open to
// any signed-in user (dashboard, self/account, surveys, api-as-service). The
// backend enforces every action regardless of what the nav/route shows.
const VIEW_PATIENTS = ['patient.read'];
const VIEW_EXAMS = ['study.read'];
const ACQUIRE_EXAMS = ['study.acquire'];
const VIEW_PACS = ['pacs_device.read'];
const VIEW_SCHEDULING = ['appointment.read'];
const VIEW_ORG_ADMIN = ['user.read', 'org_unit.read', 'role.read', 'pacs_device.manage'];

export const ProtectedPages = [
  {
    path: '/test',
    element: <TestPage />,
  },
  {
    path: '/home',
    element: <HomePage />,
  },
  {
    path: '/new-case',
    element: <NewCaseWizard />,
    requiredPermissions: ACQUIRE_EXAMS,
  },
  {
    path: '/api-as-service',
    element: <ApiAsService />,
  },
  {
    path:'/Scheduling',
    element: <SchedulingPage />,
    requiredPermissions: VIEW_SCHEDULING,
  },
  // {
  //   path: '/worklist',
  //   element: (
  //     <PlatformModeRoute allowedModes={['full']}>
  //       <WorklistManagementPage />
  //     </PlatformModeRoute>
  //   ),
  //   requiredPermissions: VIEW_SCHEDULING,
  // },
  {
    path: '/patients/new',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <AddPatientPage />
      </PlatformModeRoute>
    ),
    requiredPermissions: ['patient.create'],
  },
  {
    path: '/patients/:id/edit',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <EditPatientPage />
      </PlatformModeRoute>
    ),
    requiredPermissions: ['patient.write'],
  },
  {
    path: '/prediction-performance',
    element: <PredictionPerformancePage />,
    requiredPermissions: ['audit_log.read'],
  },
  {
    path: '/audit-trail',
    element: <AuditTrailPage />,
    requiredPermissions: ['audit_log.read'],
  },
  {
    path: '/surveys',
    element: <SurveysPage />,
  },
  {
    path: '/surveys/:surveyId',
    element: <SurveyDetailPage />,
  },
  {
    path: '/help/:guideId?',
    element: <HelpCenterPage />,
  },
  ...(process.env.NODE_ENV === 'development' ? [{
    path: '/archi',
    element: <ArchitectureDiagram />,
  }] : []),
  {
    path: '/profile/:uid',
    element: <Account />,
  },
  {
    path: '/subscription',
    element: <SubscriptionPage />,
  },
  {
    path: '/sync',
    element: <PacsSync />,
    requiredPermissions: ['pacs_device.sync'],
  },

  {
    path: '/realtime',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <RealTimeInferencingV3 />
      </PlatformModeRoute>
    ),
    requiredPermissions: ACQUIRE_EXAMS,
  },
  {
    path: '/realtimeexam',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <RealtimeExamPage />
      </PlatformModeRoute>
    ),
    requiredPermissions: ACQUIRE_EXAMS,
  },
  {
    path: '/exams',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <ExamManagement />
      </PlatformModeRoute>
    ),
    requiredPermissions: VIEW_EXAMS,
  },
  {
    path: '/pacs-inbox',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <StudyInbox />
      </PlatformModeRoute>
    ),
    requiredPermissions: VIEW_PACS,
  },
  {
    path: '/patients',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <PatientManagement />
      </PlatformModeRoute>
    ),
    requiredPermissions: VIEW_PATIENTS,
  },
  {
    path: '/patients/:id',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <PatientTimeline />
      </PlatformModeRoute>
    ),
    requiredPermissions: VIEW_PATIENTS,
  },
  {
    path: '/sweep',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <BlindSweep />
      </PlatformModeRoute>
    ),
    requiredPermissions: VIEW_EXAMS,
  },
  {
    path: '/sweep/viewer',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <BlindLoops />
      </PlatformModeRoute>
    ),
    requiredPermissions: VIEW_EXAMS,
  },
  {
    path: '/exam/viewer',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <AiAccelerate />
      </PlatformModeRoute>
    ),
    requiredPermissions: VIEW_EXAMS,
  },

  {
    path: '/organization',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <OrganizationPage />
      </PlatformModeRoute>
    ),
    requiredPermissions: VIEW_ORG_ADMIN,
  },
  {
    path: '/org-structure',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <OrgStructurePage />
      </PlatformModeRoute>
    ),
    requiredPermissions: ['org_unit.read'],
  },
  {
    path: '/roles-permissions',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <RolesPermissionsPage />
      </PlatformModeRoute>
    ),
    requiredPermissions: ['role.read'],
  },
  {
    path: '/2fa-setup',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <TwoFactorSetupPage />
      </PlatformModeRoute>
    ),
  },
  {
    path: '/feature-flags-debug',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <FeatureFlagsDebug />
      </PlatformModeRoute>
    ),
    requiredPermissions: ['role.manage'],
  },
  {
    path: '/exam-analytics',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <BsExamAnalyticsPage />
      </PlatformModeRoute>
    ),
  },
  {
    path: '/playground',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <QuickInferencePage />
      </PlatformModeRoute>
    ),
  },
  {
    path: '/playground/:sessionId',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <QuickInferenceViewerPage />
      </PlatformModeRoute>
    ),
  },
  {
    path: '/quick-inference',
    element: <Navigate to="/playground" replace />,
  },
  {
    path: '/quick-inference/:sessionId',
    element: <QuickInferenceLegacyRedirect />,
  },
];
